import type { SoundPack } from './catalog';

export type SoundCharacter = SoundPack['character'];

export interface SoundCharacterProfile {
  pitchScale: number;
  brightnessScale: number;
  decayScale: number;
  toneGain: number;
  noiseGain: number;
  noiseHighpassScale: number;
}

const CHARACTER_PROFILES: Record<SoundCharacter, SoundCharacterProfile> = {
  mechanical: {
    pitchScale: 1,
    brightnessScale: 0.9,
    decayScale: 0.9,
    toneGain: 0.96,
    noiseGain: 1.18,
    noiseHighpassScale: 0.92,
  },
  electronic: {
    pitchScale: 1,
    brightnessScale: 1.16,
    decayScale: 0.82,
    toneGain: 1.06,
    noiseGain: 0.58,
    noiseHighpassScale: 1.28,
  },
  fantasy: {
    pitchScale: 1.02,
    brightnessScale: 1.02,
    decayScale: 1.28,
    toneGain: 1.08,
    noiseGain: 0.38,
    noiseHighpassScale: 1.18,
  },
  spooky: {
    pitchScale: 0.93,
    brightnessScale: 0.7,
    decayScale: 1.42,
    toneGain: 1,
    noiseGain: 0.82,
    noiseHighpassScale: 0.68,
  },
  casino: {
    pitchScale: 1.01,
    brightnessScale: 1.12,
    decayScale: 1.16,
    toneGain: 1.08,
    noiseGain: 0.48,
    noiseHighpassScale: 1.22,
  },
  cartoon: {
    pitchScale: 1.04,
    brightnessScale: 1.08,
    decayScale: 0.92,
    toneGain: 1.1,
    noiseGain: 1.02,
    noiseHighpassScale: 1.06,
  },
};

export function soundCharacterProfile(character: SoundCharacter): SoundCharacterProfile {
  return CHARACTER_PROFILES[character];
}

/**
 * Preserve the authored motion of each cue while changing the harmonic texture
 * enough that two packs do not merely sound like pitch-shifted copies.
 */
export function waveformForCharacter(
  character: SoundCharacter,
  requested: OscillatorType,
): OscillatorType {
  switch (character) {
    case 'mechanical':
      return requested;
    case 'electronic':
      if (requested === 'triangle') return 'square';
      if (requested === 'sine') return 'triangle';
      return requested;
    case 'fantasy':
      if (requested === 'square' || requested === 'sawtooth') return 'triangle';
      if (requested === 'triangle') return 'sine';
      return requested;
    case 'spooky':
      if (requested === 'triangle') return 'sine';
      if (requested === 'square') return 'sawtooth';
      return requested;
    case 'casino':
      if (requested === 'square' || requested === 'sawtooth') return 'triangle';
      return requested;
    case 'cartoon':
      if (requested === 'triangle') return 'square';
      return requested;
  }
}
